/* ------------------------------------------------------------------ *
 * chr-vehicle-filter.js — Canada Hitch Rack
 *
 * Carries the shopper's selected vehicle across the whole site.
 *
 * EasySearch tags products with `2020-ford-f-150-esi2105207`, and
 * Shopify's legacy tag route `/collections/<handle>/<tag>` filters on
 * it. That route is NOT subject to the 5,000-product cap that silently
 * disables storefront metafield filters, which is why the whole design
 * hangs off tags rather than `?filter.p.m.*`.
 *
 * What this file does:
 *   1. Notices a vehicle tag in the URL and remembers it.
 *   2. Rewrites collection links so the vehicle follows the shopper.
 *   3. Feeds the banner rendered by `chr-vehicle-chip.liquid`.
 *
 * Deliberately NOT strict filtering. Universal parts — hitch-mount bike
 * racks, ball mounts, straps — carry no fitment data and therefore no
 * vehicle tag, so a hard filter hides them (bike racks drop 94 -> 1 on
 * an F-150). Every filtered page keeps a one-click way back to the full
 * list, and the shopper is always told what is being hidden.
 * ------------------------------------------------------------------ */

(function () {
  'use strict';

  var KEY = 'chr:vehicle';

  /* EasySearch tag shape: <year>-<make>-<model>-esi<digits>. The esi
     suffix is what makes this safe to pattern-match — no human tag
     looks like it. Note the numeric id changes on every re-import, so
     nothing anywhere may hardcode one. */
  var TAG_RE = /^\d{4}-[a-z0-9][a-z0-9-]*-esi\d+$/;

  /* Makes whose names are acronyms; title-casing would give "Gmc".
     "Ram" is deliberately absent — EasySearch's own dropdown spells it
     "Ram", and the two labels must agree. */
  var ACRONYMS = { gmc: 'GMC', bmw: 'BMW', gmt: 'GMT' };

  /* ------------------------------ store ------------------------------ */

  function read() {
    try {
      var v = JSON.parse(localStorage.getItem(KEY) || 'null');
      return v && TAG_RE.test(v.tag || '') ? v : null;
    } catch (e) {
      return null;
    }
  }

  function write(tag, label) {
    try {
      localStorage.setItem(KEY, JSON.stringify({ tag: tag, label: label }));
    } catch (e) {
      /* Private browsing, or storage disabled. Filtering still works for
         this page because the tag lives in the URL; it just will not
         follow the shopper. Failing quietly is correct here. */
    }
  }

  function clearVehicle() {
    try {
      localStorage.removeItem(KEY);
    } catch (e) {
      /* ignore */
    }
  }

  /* ------------------------------ labels ----------------------------- */

  /* Used only when we have a tag but never saw the widget that produced
     it — a shared or bookmarked link. A label captured from EasySearch
     always wins, because it is the real catalogue string. */
  function labelFromTag(tag) {
    var body = tag.replace(/-esi\d+$/, '');
    var words = body.split('-').map(function (w) {
      if (ACRONYMS[w]) return ACRONYMS[w];
      /* Short all-consonant tokens are trim codes, not words: HD, XLT,
         TRD, SRW. Title-casing them reads as a typo. */
      if (w.length <= 4 && /^[a-z]+$/.test(w) && !/[aeiou]/.test(w)) return w.toUpperCase();
      return w.charAt(0).toUpperCase() + w.slice(1);
    });
    var out = words.join(' ');
    /* Rejoin split model numbers: "F 150" -> "F-150", "C 1500" -> "C-1500". */
    out = out.replace(/\b([A-Z])\s(\d{2,4})\b/g, '$1-$2');
    return out;
  }

  /* ------------------------------- URL ------------------------------- */

  function tagInPath(pathname) {
    var m = pathname.match(/^\/collections\/([^/]+)\/([^/?#]+)\/?$/);
    if (!m) return null;
    var tag = decodeURIComponent(m[2]).toLowerCase();
    return TAG_RE.test(tag) ? { handle: m[1], tag: tag } : null;
  }

  /* Append the vehicle to a collection URL, leaving everything else
     alone. Returns null when the link must not be touched. */
  function withVehicle(href, tag) {
    var url;
    try {
      url = new URL(href, window.location.origin);
    } catch (e) {
      return null;
    }
    if (url.origin !== window.location.origin) return null;

    /* Pagination links are `/collections/<handle>?page=2`, which matches the
       shape below exactly. Rewriting them would switch the shopper to a
       different list halfway through browsing — and it broke continuous
       loading, which followed those hrefs and landed on page 2 of the
       filtered list instead of the one on screen. Paging within a filtered
       collection needs no help: Dawn already builds those URLs with the tag
       in them. */
    if (url.searchParams.has('page')) return null;

    var parts = url.pathname.replace(/\/+$/, '').split('/');
    /* ['', 'collections', '<handle>'] and nothing more. This is what
       excludes product URLs (/collections/x/products/y) and links that
       already carry a tag. */
    if (parts.length !== 3 || parts[1] !== 'collections') return null;
    if (!parts[2]) return null;

    url.pathname = '/collections/' + parts[2] + '/' + tag;
    return url.pathname + url.search + url.hash;
  }

  /* ---------------------------- link rewrite -------------------------- */

  function rewriteLinks(root, tag) {
    var links = root.querySelectorAll('a[href*="/collections/"]');
    for (var i = 0; i < links.length; i++) {
      var a = links[i];
      if (a.hasAttribute('data-chr-no-vehicle')) continue;
      var next = withVehicle(a.getAttribute('href'), tag);
      if (next) {
        a.setAttribute('href', next);
        a.setAttribute('data-chr-vehicle-applied', '');
      }
    }
  }

  /* --------------------------- the chip ------------------------------- */

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function renderChip(host, state) {
    var handle = host.getAttribute('data-handle');
    var urlTag = host.getAttribute('data-vtag') || '';
    var shown = parseInt(host.getAttribute('data-count'), 10);
    var total = parseInt(host.getAttribute('data-total'), 10);
    var saved = state.saved;

    host.textContent = '';

    if (urlTag) {
      /* Filtered. Say what is hidden and offer the way out. */
      var label = saved && saved.tag === urlTag ? saved.label : labelFromTag(urlTag);
      var row = el('div', 'chr-vchip__row');

      var badge = el('span', 'chr-vchip__badge');
      badge.appendChild(el('span', 'chr-vchip__dot'));
      badge.appendChild(el('span', 'chr-vchip__label', 'Fits your ' + label));
      row.appendChild(badge);

      if (!isNaN(shown) && !isNaN(total) && total > 0) {
        row.appendChild(el('span', 'chr-vchip__count', shown + ' of ' + total + ' shown'));
      }

      var all = el('a', 'chr-vchip__action', !isNaN(total) ? 'Show all ' + total : 'Show all');
      all.href = '/collections/' + handle;
      all.setAttribute('data-chr-no-vehicle', '');
      row.appendChild(all);

      host.appendChild(row);

      if (shown === 0) {
        host.appendChild(
          el(
            'p',
            'chr-vchip__note',
            'Nothing in this category is listed as fitting your ' +
              label +
              '. Universal parts that fit any vehicle are not tagged, so try “Show all”.'
          )
        );
      }
      host.hidden = false;
      return;
    }

    /* Unfiltered, but we know their vehicle — offer to narrow. */
    if (saved) {
      var row2 = el('div', 'chr-vchip__row');
      row2.appendChild(el('span', 'chr-vchip__label', 'Your vehicle: ' + saved.label));

      var only = el('a', 'chr-vchip__action chr-vchip__action--primary', 'Show only parts that fit');
      only.href = '/collections/' + handle + '/' + saved.tag;
      only.setAttribute('data-chr-no-vehicle', '');
      row2.appendChild(only);

      var drop = el('button', 'chr-vchip__clear');
      drop.type = 'button';
      drop.textContent = 'Clear';
      drop.addEventListener('click', function () {
        clearVehicle();
        var at = tagInPath(window.location.pathname);
        if (at) {
          window.location.href = '/collections/' + at.handle + window.location.search;
        } else {
          window.location.reload();
        }
      });
      row2.appendChild(drop);

      host.appendChild(row2);
      host.hidden = false;
    }
  }

  /* --------------------------- header chip --------------------------- */

  /* The vehicle has to be visible on every page, not just the ones that
     filter by it — otherwise a shopper cannot tell whether a filter is on
     or how to change it. Rendered from localStorage into the utility bar,
     which sits in the header group and therefore appears site-wide. */
  function renderGarage(saved) {
    var host = document.querySelector('[data-chr-garage]');
    if (!host) return;

    host.textContent = '';

    if (!saved) {
      /* No vehicle yet — the bar should invite one rather than sit empty.
         This is the only always-visible entry point to the picker, so a
         shopper who lands on a product page can start from fitment
         instead of having to find their way back to the homepage. */
      var add = document.createElement('a');
      add.className = 'chr-util__add';
      add.href = '/';
      add.setAttribute('data-chr-no-vehicle', '');
      add.textContent = '+ Add your vehicle';
      add.title = 'Pick your vehicle to see only parts that fit';
      add.addEventListener('click', function (e) {
        /* Already on the homepage: the picker is right there, so scroll to
           it rather than reloading the page the shopper is looking at. */
        var card = document.querySelector('.chr-fit__card');
        if (card && window.location.pathname === '/') {
          e.preventDefault();
          card.scrollIntoView({ behavior: 'smooth', block: 'center' });
          var sel = card.querySelector('select');
          if (sel) setTimeout(function () { sel.focus(); }, 400);
        }
      });
      host.appendChild(add);
      host.hidden = false;
      return;
    }

    var icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('viewBox', '0 0 24 24');
    icon.setAttribute('width', '14');
    icon.setAttribute('height', '14');
    icon.setAttribute('fill', 'none');
    icon.setAttribute('stroke', 'currentColor');
    icon.setAttribute('stroke-width', '2');
    icon.setAttribute('aria-hidden', 'true');
    var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M3 13l2-6h14l2 6M5 13h14v5H5z');
    icon.appendChild(path);
    host.appendChild(icon);

    /* The label doubles as the way back to everything that fits, and that
       page carries the picker for switching vehicle. */
    var link = document.createElement('a');
    link.className = 'chr-util__garage-label';
    link.href = '/collections/all/' + saved.tag;
    link.setAttribute('data-chr-no-vehicle', '');
    link.textContent = saved.label;
    link.title = 'See everything that fits your ' + saved.label;
    host.appendChild(link);

    var clear = document.createElement('button');
    clear.type = 'button';
    clear.className = 'chr-util__clear';
    clear.textContent = 'Clear';
    clear.setAttribute('aria-label', 'Clear saved vehicle');
    clear.addEventListener('click', function () {
      clearVehicle();
      /* A plain reload puts the vehicle straight back: init() adopts any
         tag it finds in the URL, and on a filtered page the tag is still
         in the path. Strip it, so clearing both forgets the vehicle and
         lands the shopper on the unfiltered list. */
      var here = tagInPath(window.location.pathname);
      if (here) {
        window.location.href = '/collections/' + here.handle + window.location.search;
      } else {
        window.location.reload();
      }
    });
    host.appendChild(clear);

    host.hidden = false;
  }

  /* ------------------------ EasySearch capture ------------------------ */

  /* EasySearch renders plain <select>s and builds an href on its search
     button. We read the visible option text at click time so the saved
     label is the real catalogue string rather than something derived
     from the tag. */
  function captureFromWidget() {
    document.addEventListener(
      'click',
      function (e) {
        var btn = e.target.closest('.easysearch-btn-search, .easysearch-btn');
        if (!btn) return;

        var href = btn.getAttribute('href') || '';
        var found = tagInPath(href.split('?')[0]);
        if (!found) return;

        var widget = btn.closest('.easysearch-widget, .easysearch-main-holder') || document;
        var parts = [];
        var sels = widget.querySelectorAll('select');
        for (var i = 0; i < sels.length; i++) {
          var s = sels[i];
          if (!s.value) continue;
          var txt = s.options[s.selectedIndex] ? s.options[s.selectedIndex].text.trim() : '';
          if (txt && !/^select /i.test(txt) && parts.indexOf(txt) === -1) parts.push(txt);
        }
        write(found.tag, parts.length ? parts.join(' ') : labelFromTag(found.tag));
      },
      true
    );
  }

  /* ------------------------------- init ------------------------------- */

  function init() {
    var here = tagInPath(window.location.pathname);
    var saved = read();

    /* A vehicle in the URL is the strongest signal there is — it is what
       the shopper is looking at right now. Adopt it. */
    if (here) {
      if (!saved || saved.tag !== here.tag) {
        saved = { tag: here.tag, label: labelFromTag(here.tag) };
        write(saved.tag, saved.label);
      }
    }

    if (saved) {
      rewriteLinks(document, saved.tag);

      /* Menus and drawers mount after first paint. */
      var mo = new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          var added = records[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            if (added[j].nodeType === 1) rewriteLinks(added[j], saved.tag);
          }
        }
      });
      mo.observe(document.documentElement, { childList: true, subtree: true });
    }

    renderGarage(saved);

    var host = document.querySelector('[data-chr-vchip]');
    if (host) renderChip(host, { saved: saved });

    captureFromWidget();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
